import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, Quotes, PenNib, ChartLineUp, Globe, Heart, Star } from "@phosphor-icons/react";
import Footer from "@/components/Footer";

const HERO_IMG = "https://images.unsplash.com/photo-1610496571096-8367bdbbae2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmUlMjBibGFjayUyMGFuZ2Uwd2hpdGV8ZWp8MHx8fHwxNzc4MDM4MTQ4fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Globe, title: "Your URL, your stage", body: "Every author gets a personal site at /yourname. No subdomain gymnastics." },
  { icon: PenNib, title: "Editor that gets out of the way", body: "A WYSIWYG editor with the texture of a typewriter and the speed of plain text." },
  { icon: ChartLineUp, title: "Quiet, useful analytics", body: "Views, likes, and comments — without the dashboard fatigue." },
  { icon: Heart, title: "Readers can react", body: "Built-in likes and threaded comments on every essay." },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/articles", { params: { is_featured: true, limit: 3 } });
        setFeatured(data.articles || []);
      } catch (e) { }
    })();
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-32 lg:pb-40 grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="eyebrow text-muted-foreground mb-6"
            >
              Vol. 01 — A new home for writers
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-serif font-black text-4xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight"
            >
              Write like it<br />
              <span className="italic font-medium">matters.</span><br />
              Publish like it<br />
              already does.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed"
            >
              Inkspot gives every author a hand-tailored, multi-tenant blog at <span className="font-mono text-foreground">/yourname</span>.
              Write essays. Add cover art. Watch your readership grow — without the SaaS sludge.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link to="/register" data-testid="hero-cta-register" className="inline-flex items-center gap-2 bg-foreground text-background px-7 py-4 text-sm font-medium hover:bg-foreground/90 transition-colors">
                Claim your URL <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/explore" data-testid="hero-cta-explore" className="inline-flex items-center gap-2 border border-foreground px-7 py-4 text-sm font-medium hover:bg-foreground/5 transition-colors">
                Read what's published
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="col-span-12 lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] border border-border overflow-hidden">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover grayscale" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="eyebrow text-background/90 mix-blend-difference">Issue 001</div>
                  <div className="font-serif text-2xl text-background mix-blend-difference">"Light reading, heavy thinking."</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 -left-8 hidden lg:block bg-foreground text-background p-6 max-w-[260px]">
              <Quotes size={20} weight="duotone" className="mb-2" />
              <p className="text-sm leading-relaxed">
                "I've moved my essays here. The reader-first design feels like a magazine, not a feed."
              </p>
              <div className="mt-3 eyebrow opacity-70">— A founding writer</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE / VALUE LINE */}
      <section className="border-b border-border bg-foreground text-background overflow-hidden">
        {/* Mobile: Scrolling Marquee */}
        <div className="flex md:hidden animate-marquee py-5 text-[10px] uppercase tracking-[0.25em] font-black">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-10 px-5">
              <span>SSR-ready public sites</span>
              <span>/</span>
              <span>SEO-friendly URLs</span>
              <span>/</span>
              <span>Per-author themes</span>
              <span>/</span>
              <span>Likes &amp; comments</span>
              <span>/</span>
            </div>
          ))}
        </div>
        {/* Desktop: Static Justified Line */}
        <div className="hidden md:flex max-w-7xl mx-auto px-6 lg:px-10 py-6 items-center justify-between text-xs uppercase tracking-[0.25em] font-black w-full">
          <span>SSR-ready public sites</span>
          <span>/</span>
          <span>SEO-friendly URLs</span>
          <span>/</span>
          <span>Per-author themes</span>
          <span>/</span>
          <span>Likes &amp; comments</span>
        </div>
      </section>

      {/* FEATURED SELECTION */}
      {featured.length > 0 && (
        <section className="border-b border-border bg-card">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="eyebrow text-muted-foreground mb-3 flex items-center gap-2">
                  <Star size={14} weight="fill" className="text-yellow-500" /> Editor's Choice
                </div>
                <h2 className="font-serif text-4xl font-black tracking-tight">The Featured Selection</h2>
              </div>
              <Link to="/articles" className="link-underline text-sm hidden sm:block">View all articles</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featured.map((a) => (
                <Link key={a.id} to={`/${a.author.username}/${a.slug}`} className="group block">
                  {a.cover_image ? (
                    <div className="aspect-[16/9] border border-border overflow-hidden mb-4">
                      <img src={fileUrl(a.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-secondary border border-border mb-4" />
                  )}
                  <div className="eyebrow text-[10px] text-muted-foreground flex items-center gap-2">
                    {a.category?.name && <span className="uppercase tracking-widest text-foreground">{a.category.name}</span>}
                    {a.category?.name && <span>·</span>}
                    <span>{a.reading_time || 1} min read</span>
                  </div>
                  <h3 className="font-serif text-2xl font-bold mt-2 leading-tight group-hover:underline decoration-1 underline-offset-4">{a.title}</h3>
                  <div className="mt-4 flex items-center gap-2">
                    {a.author.profile_picture ? (
                      <img src={fileUrl(a.author.profile_picture)} alt="" className="h-5 w-5 rounded-full object-cover" />
                    ) : (
                      <div className="h-5 w-5 bg-secondary rounded-full grid place-items-center text-[8px]">{a.author.username[0]}</div>
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-wider">{a.author.name || a.author.username}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURES TETRIS */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-16">
            <div className="col-span-12 lg:col-span-5">
              <div className="eyebrow text-muted-foreground mb-4">What you get</div>
              <h2 className="font-serif text-4xl sm:text-5xl font-black tracking-tight leading-tight">
                A studio for serious writers, dressed for readers.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 text-base text-muted-foreground leading-relaxed">
              Inkspot is a multi-tenant publishing platform. Each author owns a personal site, a private dashboard, and an audience —
              all rendered with editorial care. Your articles are SEO-friendly, your readers can engage, and your site stays yours.
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`p-8 border border-border bg-card lift-card ${i === 0 ? "col-span-12 md:col-span-7" : ""} ${i === 1 ? "col-span-12 md:col-span-5" : ""} ${i === 2 ? "col-span-12 md:col-span-5" : ""} ${i === 3 ? "col-span-12 md:col-span-7" : ""}`}
                data-testid={`feature-card-${i}`}
              >
                <f.icon size={28} weight="duotone" />
                <div className="font-serif text-2xl font-bold mt-6 leading-tight">{f.title}</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <h3 className="font-serif text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
              Your byline,<br />your domain,<br /><span className="italic font-medium">your terms.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 flex md:justify-end items-end">
            <Link to="/register" data-testid="footer-cta-register" className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-5 text-sm font-medium hover:bg-foreground/90 transition-colors">
              Start your journal <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
